import React, { useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgSave from '@icons/save.svg?react';
import SvgSliders from '@icons/sliders-filled.svg?react';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgReset from '@icons/reset-filled.svg?react';
import { FilterBarProps, FilterConfig } from '../types';
import { FilterTag } from '../FilterTag';
import { FilterChipControl } from './FilterChipControl';
import { SortChip } from './SortChip';
import { AddFilterMenu } from './AddFilterMenu';
import { AllFiltersSidebar } from './AllFiltersSidebar';
import { useHeartedFilters } from './useHeartedFilters';
import { FilterBarDensityContext } from './filterBarDensity';
import { defaultHeartedKeys, getFilterKey, isFilterActive } from './filterChipUtils';

/**
 * v2 chip-based filtering bar.
 * Renders a chip per active filter plus the "hearted" (pinned) default fields, a "+ Přidat filtr"
 * control for the rest, the sort chip, saved-filter pills, and the "Všechny filtry" sidebar.
 */
export const FilterBarV2 = <TFilter,>({
  filtersConf = [],
  filterName,
  filter,
  applyFilterValue,
  applyFilter,
  savedFilters = [],
  savingEnabled,
  loading,
  setExpandedFilters,
  onSaveFilter,
  onClearFilter,
  onManageFilters,
  sort,
  applySorting,
  sortableColumns,
  variant = 'default',
  onOpenAllFilters,
}: FilterBarProps<TFilter>) => {
  const compact = variant === 'compact';
  const { tr } = useTranslationLgs();
  // Default chips mirror the legacy bar's always-visible "primary" set: the first 7 filters
  // (desktop's non-collapsible row — the rest sat behind "Show filters"). `defaultShown` overrides
  // that positional default: `true` pins a filter beyond the prefix, `false` keeps one inside the
  // prefix out of the default set (addable via "+ Add filter"); undefined = positional. This only
  // seeds a user's initial hearted set; once they toggle any heart, their own choice persists instead.
  const defaultKeys = defaultHeartedKeys(filtersConf);
  const { isHearted, toggleHearted } = useHeartedFilters(filterName, defaultKeys);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [allFiltersOpen, setAllFiltersOpen] = useState(false);

  // Chips shown in the bar = active filters ∪ hearted fields. The rest live behind "+ Add filter".
  const shownInBar = (conf: FilterConfig) => isFilterActive(conf, filter) || isHearted(getFilterKey(conf));

  const barConfs = filtersConf.filter(c => shownInBar(c) && getFilterKey(c) !== pendingKey);
  const pendingConf = pendingKey ? filtersConf.find(c => getFilterKey(c) === pendingKey) : null;
  const addableConfs = filtersConf.filter(c => !shownInBar(c) && getFilterKey(c) !== pendingKey);
  const anyActive = filtersConf.some(c => isFilterActive(c, filter));

  // With no configured filters there's nothing to add, reset, save, or open in the "All filters"
  // sidebar — so suppress all filter controls. The sort chip self-hides when there are no sortable
  // columns, so if there are neither filters nor sort nor saved filters, render no bar at all.
  const hasFilters = filtersConf.length > 0;
  const hasSort = !!applySorting && !!sortableColumns && sortableColumns.length > 0;
  if (!hasFilters && !hasSort && savedFilters.length === 0) return null;

  return (
    <div className={clsx("font-['DM Sans'] flex flex-col", !compact && 'rounded-t-lg bg-content-quaternary')}>
      {/* Saved filter pills */}
      {savedFilters.length > 0 && (
        <>
          <div className="py-1 px-4 flex flex-wrap items-center gap-2">
            {savedFilters.map(savedFilter => (
              <FilterTag
                key={savedFilter.id}
                label={savedFilter.name}
                onClick={() => {
                  // Apply the RAW saved filter; normalization happens once at fetch time.
                  applyFilter(savedFilter.data);
                  if (Object.values(savedFilter.data).some(value => value !== null && value !== '' && (!Array.isArray(value) || value.length > 0))) {
                    setExpandedFilters?.(true);
                  }
                }}
              />
            ))}
            <RingilButton
              mode="link"
              icon={<SvgPencil className="w-4 h-4" />}
              tooltip={tr('RingilDataTable.manageFilters', 'Manage saved filters')}
              onClick={onManageFilters}
              dataTestId="manage-filters"
              className="ml-auto"
            />
          </div>
          <hr className="mx-4 m-0 border-t border-solid border-content-negativePrimary" />
        </>
      )}

      {/* Density reaches this row only: the chips in the "All filters" sidebar below keep their size. */}
      <FilterBarDensityContext.Provider value={compact ? 'compact' : 'default'}>
        <div className={clsx('flex flex-wrap items-center gap-2', compact ? 'py-3.5' : 'py-2 px-4')}>
          <SortChip sort={sort} applySorting={applySorting} sortableColumns={sortableColumns} />

          {barConfs.map(conf => (
            <FilterChipControl key={getFilterKey(conf)} conf={conf} filter={filter} applyFilterValue={applyFilterValue} loading={loading} />
          ))}

          {pendingConf && (
            <FilterChipControl
              key={`pending-${pendingKey}`}
              conf={pendingConf}
              filter={filter}
              applyFilterValue={applyFilterValue}
              loading={loading}
              autoOpen
              onClosed={() => setPendingKey(null)}
            />
          )}

          <AddFilterMenu inactiveConfs={addableConfs} onAdd={setPendingKey} />

          {anyActive && (
            <RingilButton
              mode="link"
              label={tr('RingilDataTable.resetFilter', 'Reset')}
              icon={<SvgReset className="w-3.5 h-3.5" />}
              onClick={onClearFilter}
              className={clsx('!text-complementary-error hover:!text-content-brand', compact && 'h-6 p-0 text-xs')}
              dataTestId="reset-filters"
            />
          )}

          {/* Right-aligned controls — only meaningful when there are filters to manage. */}
          {hasFilters && (
            <div className="ml-auto flex items-center gap-0.5">
              {compact ? (
                <RingilButton
                  mode="filled-rectangle"
                  label={tr('RingilDataTable.allFilters', 'All filters')}
                  icon={<SvgSliders className="w-4 h-4" />}
                  onClick={() => (onOpenAllFilters ? onOpenAllFilters() : setAllFiltersOpen(true))}
                  dataTestId="all-filters"
                  // Same 24px as the compact chips beside it (Figma 21940:60909).
                  className="h-6 gap-2.5 px-3 rounded text-xs shadow-none [&_svg]:w-3 [&_svg]:h-3"
                />
              ) : (
                <RingilButton
                  mode="link"
                  tooltip={tr('RingilDataTable.allFilters', 'All filters')}
                  icon={<SvgSliders className="w-4 h-4" />}
                  onClick={() => (onOpenAllFilters ? onOpenAllFilters() : setAllFiltersOpen(true))}
                  dataTestId="all-filters"
                />
              )}
              {savingEnabled && (
                <>
                  <span className="w-px h-5 self-center bg-content-tertiary" />
                  <RingilButton
                    mode="link"
                    icon={<SvgSave className="w-4 h-4" />}
                    tooltip={tr('RingilDataTable.saveFilter', 'Save filter')}
                    onClick={onSaveFilter}
                    disabled={!anyActive}
                    dataTestId="save-filter"
                  />
                </>
              )}
            </div>
          )}
        </div>
      </FilterBarDensityContext.Provider>

      {hasFilters && !onOpenAllFilters && (
        <AllFiltersSidebar
          visible={allFiltersOpen}
          onClose={() => setAllFiltersOpen(false)}
          filtersConf={filtersConf}
          filter={filter}
          applyFilterValue={applyFilterValue}
          loading={loading}
          isHearted={isHearted}
          toggleHearted={toggleHearted}
          onReset={onClearFilter}
          onSaveFilter={onSaveFilter}
          savingEnabled={savingEnabled}
          sort={sort}
          applySorting={applySorting}
          sortableColumns={sortableColumns}
        />
      )}
    </div>
  );
};
